import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";

interface FieldEngineer {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  specialization?: string;
  isActive: boolean;
  activeServiceOrders?: number;
  createdAt?: string;
}

interface FieldEngineersResponse {
  success: boolean;
  data?: FieldEngineer[];
}

interface EngineerFormData {
  name: string;
  phone: string;
  email: string;
  specialization: string;
  isActive: string;
}

const emptyForm: EngineerFormData = {
  name: "",
  phone: "",
  email: "",
  specialization: "",
  isActive: "true",
};

const FieldEngineers: React.FC = () => {
  const [engineers, setEngineers] = useState<FieldEngineer[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [formData, setFormData] = useState<EngineerFormData>(emptyForm);
  const [search, setSearch] = useState<string>("");
  const [hasFetchedEngineers, setHasFetchedEngineers] = useState<boolean>(false);
  const hasAutoLoadedRef = useRef<boolean>(false);

  const fetchEngineers = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await fetch("https://backend.voltreum.com/api/v2/field-engineers");
      if (!response.ok) {
        throw new Error(`Failed with status ${response.status}`);
      }
      const result: FieldEngineersResponse = await response.json();
      const list = Array.isArray(result?.data) ? result.data : [];
      setEngineers(list);
    } catch (error) {
      console.error("Failed to fetch all field engineers, falling back to active list:", error);
      try {
        const fallbackResponse = await fetch(
          "https://backend.voltreum.com/api/v2/field-engineers?activeOnly=true"
        );
        const fallbackResult: FieldEngineersResponse = await fallbackResponse.json();
        const list = Array.isArray(fallbackResult?.data) ? fallbackResult.data : [];
        setEngineers(list);
      } catch (fallbackError) {
        console.error("Failed to fetch field engineers:", fallbackError);
        setEngineers([]);
      }
    } finally {
      setIsLoading(false);
      setHasFetchedEngineers(true);
    }
  };

  const filteredEngineers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return engineers;
    return engineers.filter((engineer) => {
      return (
        engineer.name.toLowerCase().includes(query) ||
        (engineer.email || "").toLowerCase().includes(query) ||
        (engineer.phone || "").toLowerCase().includes(query) ||
        (engineer.specialization || "").toLowerCase().includes(query)
      );
    });
  }, [engineers, search]);

  useEffect(() => {
    if (hasAutoLoadedRef.current) return;
    hasAutoLoadedRef.current = true;
    void fetchEngineers();
  }, []);

  const startEdit = (engineer: FieldEngineer): void => {
    setShowForm(true);
    setEditingId(engineer.id);
    setFormData({
      name: engineer.name || "",
      phone: engineer.phone || "",
      email: engineer.email || "",
      specialization: engineer.specialization || "",
      isActive: engineer.isActive ? "true" : "false",
    });
  };

  const resetForm = (): void => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowForm(false);
  };

  const handleSave = async (): Promise<void> => {
    if (!formData.name.trim()) return;
    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        specialization: formData.specialization.trim(),
        isActive: formData.isActive === "true",
      };

      const endpoint = editingId
        ? `https://backend.voltreum.com/api/v2/field-engineers/${editingId}`
        : "https://backend.voltreum.com/api/v2/field-engineers";
      const method = editingId ? "PUT" : "POST";

      const response = await fetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed with status ${response.status}`);
      }

      await fetchEngineers();
      resetForm();
    } catch (error) {
      console.error("Failed to save field engineer:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (engineer: FieldEngineer): Promise<void> => {
    setIsSubmitting(true);
    try {
      const payload = {
        name: engineer.name || "",
        phone: engineer.phone || "",
        email: engineer.email || "",
        specialization: engineer.specialization || "",
        isActive: !engineer.isActive,
      };

      const response = await fetch(
        `https://backend.voltreum.com/api/v2/field-engineers/${engineer.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed with status ${response.status}`);
      }

      await fetchEngineers();
    } catch (error) {
      console.error("Failed to update field engineer status:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle>Field Engineers</CardTitle>
          <Button
            type="button"
            className="bg-green-600 hover:bg-green-700 text-white"
            onClick={() => {
              setShowForm(true);
              setEditingId(null);
              setFormData(emptyForm);
            }}
          >
            + Add Engineer
          </Button>
        </CardHeader>
        <CardContent>
          <Dialog
            open={showForm}
            onOpenChange={(open) => {
              if (!open) resetForm();
            }}
          >
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingId ? "Edit Field Engineer" : "Add Field Engineer"}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-1">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="engineer-name">Name</Label>
                  <Input
                    id="engineer-name"
                    placeholder="Enter engineer name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, name: e.target.value }))
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="engineer-phone">Phone</Label>
                  <Input
                    id="engineer-phone"
                    placeholder="Enter phone number"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, phone: e.target.value }))
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="engineer-email">Email</Label>
                  <Input
                    id="engineer-email"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, email: e.target.value }))
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="engineer-specialization">Specialization</Label>
                  <Input
                    id="engineer-specialization"
                    placeholder="Enter specialization"
                    value={formData.specialization}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, specialization: e.target.value }))
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={formData.isActive}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, isActive: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Active</SelectItem>
                      <SelectItem value="false">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="button" onClick={handleSave} disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : editingId ? "Update Engineer" : "Add Engineer"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <div className="flex flex-col sm:flex-row gap-4 mb-4 items-center">
            <Input
              placeholder="Search by name, email, phone, specialization..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1"
            />
            <Button type="button" variant="outline" onClick={fetchEngineers} disabled={isLoading}>
              {isLoading ? "Refreshing..." : "Refresh"}
            </Button>
          </div>

          {hasFetchedEngineers && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="text-left p-3">#</th>
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3">Phone</th>
                  <th className="text-left p-3">Email</th>
                  <th className="text-left p-3">Specialization</th>
                  <th className="text-left p-3">Active Orders</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Created</th>
                  <th className="text-left p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEngineers.map((engineer, index) => (
                  <tr key={engineer.id} className="border-b last:border-b-0">
                    <td className="p-3">{index + 1}</td>
                    <td className="p-3 font-medium">{engineer.name}</td>
                    <td className="p-3">{engineer.phone || "-"}</td>
                    <td className="p-3">{engineer.email || "-"}</td>
                    <td className="p-3">{engineer.specialization || "-"}</td>
                    <td className="p-3">{engineer.activeServiceOrders || 0}</td>
                    <td className="p-3">
                      <span
                        className={
                          engineer.isActive
                            ? "px-2 py-1 rounded-md bg-green-100 text-green-800"
                            : "px-2 py-1 rounded-md bg-gray-100 text-gray-800"
                        }
                      >
                        {engineer.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="p-3">
                      {engineer.createdAt
                        ? new Date(engineer.createdAt).toLocaleString()
                        : "-"}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="h-7 px-2"
                          onClick={() => startEdit(engineer)}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          className="h-7 px-2 bg-red-600 hover:bg-red-700 text-white"
                          onClick={() => handleToggleActive(engineer)}
                          disabled={isSubmitting}
                        >
                          {engineer.isActive ? "Deactivate" : "Activate"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          {!isLoading && !hasFetchedEngineers && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Click <strong className="text-foreground">Refresh</strong> to load field engineers.
            </p>
          )}

          {!isLoading && hasFetchedEngineers && filteredEngineers.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">
              No field engineers found.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FieldEngineers;
